import Header from '@/components/Header';
import Hero from '@/components/Hero';
import ROICalculator from '@/components/ROICalculator';
import CommandCenter from '@/components/CommandCenter';
import AIFeatures from '@/components/AIFeatures';
import Modules from '@/components/Modules';
import Comparison from '@/components/Comparison';
import Workflow from '@/components/Workflow';
import Personas from '@/components/Personas';
import GlobalCTA from '@/components/GlobalCTA';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster } from 'sonner';
// Marketing components
import TimelineComparison from '@/components/TimelineComparison';
import StaffingCrisis from '@/components/StaffingCrisis';
import ProtestProof from '@/components/ProtestProof';
import WhatKeepsYouUp from '@/components/WhatKeepsYouUp';
import CollaborationChaos from '@/components/CollaborationChaos';

const HomePage = () => {
  const pageData = getDefaultData();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />
      <main>
        <Hero data={pageData.hero} />
        <ROICalculator />
        <TimelineComparison />
        <CommandCenter data={pageData.commandCenter} />
        <AIFeatures data={pageData.aiFeatures} />
        <CollaborationChaos />
        <Modules data={pageData.modules} />
        <Comparison data={pageData.comparison} />
        <StaffingCrisis />
        <ProtestProof />
        <Workflow data={pageData.workflow} />
        <WhatKeepsYouUp />
        <Personas data={pageData.personas} />
        <GlobalCTA data={pageData.globalCTA} />
      </main>
      <Footer />
    </div>
  );
};

// Default/fallback data when Sanity is not available
function getDefaultData() {
  return {
    hero: {
      headlines: [
        'Intelligence',
        'That Learns Your',
        'Acquisition Process.'
      ],
      differentiator: 'AcuSightPro is the intelligence layer above the federal system of record. We enhance established acquisition management platforms through standards-based interface contracts, never replace them.',
      subtitle: 'ACUMEN-10 learns your agency\'s patterns, compounds institutional knowledge across every team, and governs the entire acquisition lifecycle across Legal, Budget, HR, Tech, Security, and Program. Clean packages flow into the system of record. Outcomes flow back. Humans stay in command of every decision.',
      primaryCTA: { text: 'See It In Action', link: '/contact' },
      secondaryCTA: { text: 'Explore the Platform', link: '/product' },
      card: {
        title: 'Why Intelligence Beats Features',
        tag: 'Patented AI',
        summary: 'Most platforms bolt AI onto existing workflows. AcuSightPro\'s patented Adaptive Compound Intelligence learns how your agency works, compounds that knowledge, and gets smarter with every acquisition cycle.',
        features: [
          '<strong>Compound Learning:</strong> Every acquisition your agency runs makes ACUMEN-10 smarter. Patterns, risks, and institutional knowledge accumulate automatically.',
          '<strong>Human-Verified Governance:</strong> AI recommends, surfaces, and drafts. Humans approve every decision. Always.',
          '<strong>Cross-Team Intelligence:</strong> What Legal learns benefits Budget. What Security flags reaches Program. One brain, every team.',
          '<strong>Provable Audit Trail:</strong> Every AI action traced, every recommendation sourced, every decision documented. Built for oversight.'
        ]
      }
    },
    stats: [
      {
        label: 'Time to decision-grade view',
        manual: '4-8 months',
        withASP: '1-3 weeks'
      },
      {
        label: 'Actions a single CO can manage',
        manual: '1-2 actions',
        withASP: '4-8 actions'
      },
      {
        label: 'Full evaluations per person per year',
        manual: '4-8',
        withASP: '15-30'
      },
      {
        label: 'Manual "grunt work"',
        manual: '100% human',
        withASP: '70-85% automated'
      }
    ],
    commandCenter: {
      title: 'From market research to award: a Ready-to-Run Acquisition Command Center',
      subtitle: 'AcuSightPro was shaped around real acquisition pain: scattered documents, inconsistent reviews, and slow cycles. ASP standardizes the workflow end-to-end, then adds an AI Generator and Submission Portal that support RFI, RFQ, RFP, and SOW work products with explainability and audit trails.',
      features: [
        {
          question: 'One workspace to manage actions from intake through integration.',
          answer: 'A command center that tracks every effort through Intake, Fast Scan, Deep Dive, and post-award planning. One record, one trail.'
        },
        {
          question: 'Drafting support for RFI/RFQ/RFP/SOW that still feels "government correct."',
          answer: 'AI Generator converts your inputs (requirements, constraints, risk findings) into structured drafts your team edits and approves.'
        },
        {
          question: 'FAR/DFARS awareness and defensible compliance checks.',
          answer: 'Flags clause conflicts and compliance gaps, then suggests corrections with explainable rationale and a traceable record.'
        },
        {
          question: 'A structured portal to replace inbox chaos and spreadsheet triage.',
          answer: 'Automated Submission Portal: portal-ready package with attachments, naming, completeness checks, and a submission audit trail (supports full RFP proposal submissions: technical, price, past performance, etc.).'
        },
        {
          question: 'AI assistance that is explainable, auditable, and aligned to oversight.',
          answer: 'AI drafts, flags, compares, and scores. Humans approve. Every recommendation links back to the source text or data that triggered it.'
        },
        {
          question: 'Direct integration with repositories and systems of record.',
          answer: 'Secure connectors for document stores, email, financial and PR systems, and portfolio reporting without rebuilding your stack.'
        },
        {
          question: 'Configurable playbooks that adapt to different agencies, programs, and thresholds.',
          answer: 'Configurable workflows & libraries (templates, factors, checklists) so similar actions start from proven patterns, not scratch.'
        }
      ]
    },
    aiFeatures: {
      title: 'AI that learns, compounds, and organizes. Humans decide.',
      subtitle: 'Built on Adaptive Compound Intelligence (ACI), ASP\'s AI learns your acquisition patterns, compounds institutional knowledge, and converts messy inputs into structured work, without ever replacing required review, approval, or signature authority.',
      features: [
        {
          title: 'AI RFI/RFQ/RFP/SOW Generator',
          description: 'Converts requirements, constraints, and risk findings into structured drafts your team can edit and approve.'
        },
        {
          title: 'FAR/DFARS-aware drafting',
          description: 'Flags clause conflicts and compliance gaps, then suggests corrections with explainable rationale.'
        },
        {
          title: 'Automated Submission Portal',
          description: 'Portal-ready packages, attachment naming, completeness checks, and a submission audit trail (incl. full RFP proposals).'
        },
        {
          title: 'Explainable scoring & comparisons',
          description: 'AI scoring/evaluation and source selection support with traceable links back to proposal sections and evidence.'
        },
        {
          title: 'Risk & issue spotting',
          description: 'Flags anomalies and conflicts across Legal, Budget, HR, and Tech/Sec content, then routes owners and next steps.'
        },
        {
          title: 'Smart checklists',
          description: 'Dynamically updates checklists based on acquisition type, size, vendor history, and policy rules.'
        },
        {
          title: 'Draft summaries & memos',
          description: 'Generates first-draft narratives and briefings (clearly labeled) for humans to edit, approve, and reuse.'
        },
        {
          title: 'Condition-to-close tracking',
          description: 'Turns findings into owners, due dates, and a living register with reminders and audit-friendly change history.'
        }
      ]
    },
    modules: {
      title: 'Plug-in modules for acquisition diligence and execution readiness',
      subtitle: 'The platform runs on a shared risk engine and reusable libraries so every team evaluates consistently, then moves cleanly from award to execution.',
      items: [
        {
          title: 'Budget Diligence Module',
          description: 'Standardizes tax-related checks, obligations, and risk flags. Keeps finance and legal aligned with clear remediation paths.'
        },
        {
          title: 'HR & Workforce Module',
          description: 'Structures workforce diligence across org structures, key personnel, labor categories, and transition risks. Feeds conditions-to-close.'
        },
        {
          title: 'Tech & Security Module',
          description: 'Focused on systems, cybersecurity, data protection, and technical debt. Surfaces alignment gaps with security requirements.'
        },
        {
          title: 'Integration & Transition Module',
          description: 'Bridges acquisition to execution across milestones, dependencies, and handoffs, so award does not become "start over."'
        },
        {
          title: 'Vendor & Performance Module',
          description: 'Consolidates vendor history and past performance with open actions. Supports consistent comparisons and stronger source selection narratives.'
        },
        {
          title: 'Legal & Compliance Module',
          description: 'Automates FAR/DFARS compliance checks, clause validation, and protest-ready documentation. Reduces legal review cycles by 70%.'
        }
      ]
    },
    comparison: {
      title: 'How many acquisitions can your team really handle?',
      subtitle: 'Today, serious acquisition work means months of manual effort spread across disconnected teams. AcuSightPro brings everyone together in real-time with AI that actually understands federal acquisition.',
      table: [
        { metric: 'Time to decision-grade view', manual: '4-8 months', withASP: '1-3 weeks' },
        { metric: 'Actions a single CO can manage at once', manual: '1-2 actions', withASP: '4-8 actions' },
        { metric: 'Full evaluations per person per year', manual: '4-8', withASP: '15-30' },
        { metric: 'Manual "grunt work"', manual: '100% human effort', withASP: '70-85% automated' },
        { metric: 'Compliance errors caught before submission', manual: '30-40% catch rate', withASP: '95%+ catch rate' },
        { metric: 'Average staff hours saved per action', manual: 'Baseline', withASP: '40-60 hours saved' },
        { metric: 'Team coordination style', manual: 'Email chains, version chaos, waiting for handoffs', withASP: 'Single platform, real-time collaboration, zero handoff delays' }
      ]
    },
    workflow: {
      title: 'From intake to integration, fully guided.',
      subtitle: 'Wizard-driven steps with plain-language summaries, traffic-light risk, and a clear "You are here" indicator so novices don\'t get lost.',
      steps: [
        {
          label: 'Step 1',
          title: 'Intake & core screening',
          items: [
            'Ingests requirement packages, attachments, and key constraints.',
            'Builds a core profile and initial risk picture.',
            'Recommends whether to proceed to Fast Scan.'
          ]
        },
        {
          label: 'Step 2',
          title: 'Fast Scan',
          items: [
            'Runs Legal/Budget/HR/Tech/Sec modules in waves.',
            'Flags deal-killers and blocking issues early.',
            'Go / Proceed with caution / Stop summary banner.'
          ]
        },
        {
          label: 'Step 3',
          title: 'Deep Dive',
          items: [
            'Expanded checklists where risk is high or complex.',
            'Assigns owners, due dates, and remediation cost/time.',
            'Feeds a consolidated conditions-to-close view.'
          ]
        },
        {
          label: 'Step 4',
          title: 'Integration roadmap',
          items: [
            'Generates a critical-path integration plan.',
            'Tracks closing conditions and remediation progress.',
            'Surfaces integration risk in portfolio dashboards.'
          ]
        }
      ]
    },
    screens: {
      title: 'Screens designed for real acquisition work.',
      subtitle: 'These are the core views we\'re building for launch. All screenshots are early-stage visuals that map directly to our Figma concepts.',
      items: [
        {
          thumbnail: 'https://acusightpro.com/wp-content/uploads/2025/12/dashthumbnail-e1764589990435.png',
          fullImage: 'https://acusightpro.com/wp-content/uploads/2025/12/Dashboard-new-Full-scaled.png',
          title: 'Acquisition Copilot',
          caption: 'Unified chat, extracted fields, summaries, and SOW generation in one guided workspace.'
        },
        {
          thumbnail: 'https://acusightpro.com/wp-content/uploads/2025/12/evaluationsthumbnail.png',
          fullImage: 'https://acusightpro.com/wp-content/uploads/2025/12/Evaluation-Room-new-Full-scaled.png',
          title: 'Evaluation Room',
          caption: 'Structured evaluation, scoring, and risk review across Legal/Budget/HR/Tech/Sec modules.'
        },
        {
          thumbnail: 'https://acusightpro.com/wp-content/uploads/2025/12/portfoliothumbnail.png',
          fullImage: 'https://acusightpro.com/wp-content/uploads/2025/12/Portfolio-Radar-new-Full-scaled.png',
          title: 'Portfolio Radar',
          caption: 'Live view of pipeline, cycle times, vendor performance, and funding risk across all active and upcoming acquisitions.'
        },
        {
          thumbnail: 'https://acusightpro.com/wp-content/uploads/2025/12/modulesthumbnail.png',
          fullImage: 'https://acusightpro.com/wp-content/uploads/2025/12/Modules-Domains-new-Full-scaled.png',
          title: 'Modules & Domains',
          caption: 'Configure Legal, Budget, HR, Tech/Sec, and Integration lanes and assign them across domains and teams.'
        }
      ]
    },
    personas: {
      title: 'Built for every seat in the acquisition room.',
      items: [
        { title: 'Acquisition leadership / HCA', description: 'Enterprise performance, risk, and funding cliff visibility.' },
        { title: 'Branch chiefs & supervisory COs', description: 'Team workload, cycle times, and high-risk actions by branch.' },
        { title: 'Contracting officers & 1102s', description: 'Personal queue, alerts, and AI-assisted clauses and templates.' },
        { title: 'Finance & budget', description: 'Obligations, under-obligation risk, and funding lapse warnings.' },
        { title: 'Program & portfolio managers', description: 'Program-centric view of actions, vendors, and performance.' },
        { title: 'Cross-functional diligence teams', description: 'Legal/Budget/HR/Tech/Sec/Integration modules in one workspace.' }
      ]
    },
    globalCTA: {
      title: 'Ready to see AcuSightPro in action?',
      subtitle: 'We\'re building an acquisition copilot, diligence engine, and command center for modern agencies. Let\'s walk you through what investors are backing.',
      buttonText: 'Contact us',
      buttonLink: '/contact'
    }
  };
}

export default HomePage;
